import type { Attempt, StrategyEvidence } from "@oefenheld/reken-core";
import type { ItemPresentation, SessionRecord } from "./schema.ts";

export function sessionAcceptsAnswers(session: SessionRecord | undefined): boolean {
  return session?.phase === "CORE_ACTIVE" || session?.phase === "BONUS_ACTIVE";
}

/** Data repair, not learning: never create evidence, remove history or refund a dose. */
export function recoverPresentationMarkers(
  presentations: readonly ItemPresentation[], sessions: readonly SessionRecord[],
  attempts: readonly Attempt[], evidence: readonly StrategyEvidence[], at: string,
): readonly ItemPresentation[] {
  const sessionById = new Map(sessions.map((s) => [s.id, s]));
  const resultTimes = new Map<string, string>();
  for (const result of [...attempts.map((a) => ({ id: a.presentationId, at: a.submittedAt })),
    ...evidence.map((e) => ({ id: e.presentationId, at: e.occurredAt }))]) {
    if (result.id) resultTimes.set(result.id, result.at);
  }
  return presentations.flatMap<ItemPresentation>((presentation) => {
    if (presentation.state !== "PRESENTED") return [];
    const owner = sessionById.get(presentation.sessionId);
    const resultAt = resultTimes.get(presentation.id);
    if (resultAt || presentation.lessonResponse) return [{ ...presentation, state: "RESOLVED" as const,
      resolvedAt: resultAt ?? presentation.resolvedAt ?? owner?.endedAt ?? at,
      recovery: { reason: "RESULT_ALREADY_RECORDED" as const, at } }];
    if (sessionAcceptsAnswers(owner)) return [];
    return [{ ...presentation, state: "ABANDONED" as const, resolvedAt: presentation.resolvedAt ?? owner?.endedAt ?? at,
      recovery: { reason: owner ? "SESSION_INACTIVE" as const : "SESSION_MISSING" as const, at } }];
  });
}

/** One choice for home, start and resume: real open work precedes a newer empty session. */
export function chooseResumableSession(
  sessions: readonly SessionRecord[], presentations: readonly ItemPresentation[], preferredId?: string,
): SessionRecord | null {
  const active = sessions.filter(sessionAcceptsAnswers);
  const activeById = new Map(active.map((s) => [s.id, s]));
  const open = presentations.filter((p) => p.state === "PRESENTED" && activeById.has(p.sessionId))
    .sort((a, b) => Date.parse(b.presentedAt) - Date.parse(a.presentedAt) || a.id.localeCompare(b.id));
  if (open.length) return activeById.get(open[0].sessionId)!;
  return activeById.get(preferredId ?? "")
    ?? [...active].sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt))[0]
    ?? [...sessions].reverse().find((s) => s.phase === "CORE_COMPLETE" || s.phase === "REVEAL_PENDING") ?? null;
}
